import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '../../../api/graphql'
import Routes from '../../../startup/routes'
import Loading from '../misc/Loading'
import AnalyticsDashboard from './AnalyticsDashboard'
import { findSubscriptionOrgById } from './billingGating'
import BillingSection from './BillingSection'
import BrandingEditor from './BrandingEditor'
import {
  BUSINESS_SUBSCRIPTION_QUERY,
  BusinessSubscriptionQueryResponse,
  humanizeSubscriptionStatus,
} from './BusinessCalendarQueries'
import EmbedCodeGenerator from './EmbedCodeGenerator'
import PastDueBanner from './PastDueBanner'
import ReactivateCalendarScreen from './ReactivateCalendarScreen'
import SocialAccountsManager from './SocialAccountsManager'
import SocialPostsManager from './SocialPostsManager'
import { Hugeicon } from '../../../icons/Hugeicon'

// Re-exported for callers/tests that imported it from this component.
export { findSubscriptionOrgById }

type Tab = 'analytics' | 'branding' | 'embed' | 'social' | 'posts' | 'billing'

const getCheckoutParams = () => {
  const params = new URLSearchParams(window.location.search)
  return {
    isPostCheckout: params.get('success') === 'true',
  }
}

const getSocialCallbackParams = () => {
  const params = new URLSearchParams(window.location.search)
  return {
    socialConnected: params.get('social_connected'),
    socialError: params.get('social_error'),
  }
}

const ONBOARDING_DISMISSED_KEY = 'civiclift_calendar_onboarding_dismissed'

const BusinessCalendarDashboard: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>()
  const socialCallback = getSocialCallbackParams()
  const checkoutParams = getCheckoutParams()
  const [activeTab, setActiveTab] = useState<Tab>(
    socialCallback.socialConnected || socialCallback.socialError ? 'social' :
    checkoutParams.isPostCheckout ? 'branding' : 'analytics'
  )
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem(`${ONBOARDING_DISMISSED_KEY}_${organizationId}`) === 'true'
  )
  const [socialMessage, setSocialMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(() => {
    if (socialCallback.socialConnected && Number(socialCallback.socialConnected) > 0) {
      return { type: 'success', text: `Successfully connected ${socialCallback.socialConnected} social account(s).` }
    }
    if (socialCallback.socialError) {
      return { type: 'error', text: socialCallback.socialError }
    }
    return null
  })
  const { data, loading, refetch } = useQuery<BusinessSubscriptionQueryResponse>(BUSINESS_SUBSCRIPTION_QUERY)
  const [waitingForWebhook, setWaitingForWebhook] = useState(checkoutParams.isPostCheckout)
  const [setupTimedOut, setSetupTimedOut] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)
  const pollCountRef = useRef(0)

  // Clean up social callback params from URL
  useEffect(() => {
    if (socialCallback.socialConnected || socialCallback.socialError) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const result = findSubscriptionOrgById(data, organizationId)
  const subscriptionOrg = result?.organization ?? null
  const isOrganizationOwner = result?.role === 'owner'
  const hasSubscription = !!subscriptionOrg?.businessSubscription

  useEffect(() => {
    if (!waitingForWebhook || hasSubscription) {
      if (hasSubscription && waitingForWebhook) {
        setWaitingForWebhook(false)
        setShowCelebration(true)
        window.history.replaceState({}, '', window.location.pathname)
      }
      return
    }

    const interval = setInterval(() => {
      pollCountRef.current += 1
      if (pollCountRef.current > 20) {
        clearInterval(interval)
        setWaitingForWebhook(false)
        setSetupTimedOut(true)
        return
      }
      refetch()
    }, 3000)

    return () => clearInterval(interval)
  }, [waitingForWebhook, hasSubscription, refetch])

  // Continue polling at a slower rate after timeout
  useEffect(() => {
    if (!setupTimedOut || hasSubscription) return

    const slowInterval = setInterval(() => {
      refetch().then(refetchResult => {
        const found = findSubscriptionOrgById(refetchResult.data, organizationId)
        if (found?.organization?.businessSubscription) {
          clearInterval(slowInterval)
          setSetupTimedOut(false)
        }
      })
    }, 10000) // Poll every 10 seconds

    return () => clearInterval(slowInterval)
  }, [setupTimedOut, hasSubscription, refetch, organizationId])

  const handleCelebrationDone = useCallback(() => {
    setShowCelebration(false)
    setActiveTab('branding')
  }, [])

  const confettiPieces = useMemo(() => {
    const colors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']
    return Array.from({ length: 50 }).map((_, i) => ({
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 2}s`,
      animationDuration: `${2 + Math.random() * 2}s`,
      backgroundColor: colors[i % 6],
      width: `${6 + Math.random() * 6}px`,
      height: `${6 + Math.random() * 6}px`,
    }))
  }, [])

  if (loading && !waitingForWebhook) {
    return <Loading className="isCentered isDark" />
  }

  if (waitingForWebhook && !hasSubscription) {
    return (
      <Container>
        <SetupState>
          <SetupSpinner />
          <SetupTitle>Setting up your calendar...</SetupTitle>
          <SetupText>We&apos;re activating your subscription. This usually takes just a few seconds.</SetupText>
        </SetupState>
      </Container>
    )
  }

  if (setupTimedOut && !hasSubscription) {
    return (
      <Container>
        <NotFoundState>
          <SetupDelayIcon />
          <NotFoundTitle>Setup Taking Longer Than Expected</NotFoundTitle>
          <NotFoundText>
            Your payment was successful, but activating your calendar is taking longer than usual. This typically
            resolves within a few minutes.
          </NotFoundText>
          <SetupDelayActions>
            <RefreshButton onClick={() => window.location.reload()}>Refresh Page</RefreshButton>
            <SupportLink href="mailto:support@civiclift.com">Contact Support</SupportLink>
          </SetupDelayActions>
        </NotFoundState>
      </Container>
    )
  }

  if (showCelebration) {
    return (
      <Container>
        <CelebrationOverlay>
          <ConfettiContainer>
            {confettiPieces.map((piece, i) => (
              <ConfettiPiece key={i} style={piece} />
            ))}
          </ConfettiContainer>
          <CelebrationContent>
            <CelebrationIcon>
              <Hugeicon name="checkmark-circle" size={48} style={{ color: '#22c55e' }} />
            </CelebrationIcon>
            <CelebrationTitle>Your calendar is ready!</CelebrationTitle>
            <CelebrationText>
              Your 14-day free trial has started. Let&apos;s customize your calendar and get it on your website.
            </CelebrationText>
            <CelebrationButton onClick={handleCelebrationDone}>
              Let&apos;s get started
              <Hugeicon name="arrow-right-long" size={16} />
            </CelebrationButton>
          </CelebrationContent>
        </CelebrationOverlay>
      </Container>
    )
  }

  if (!subscriptionOrg || !subscriptionOrg.businessSubscription) {
    return (
      <Container>
        <NotFoundState>
          <NotFoundTitle>Calendar Not Found</NotFoundTitle>
          <NotFoundText>This calendar doesn&apos;t exist or you don&apos;t have access to it.</NotFoundText>
          <BackToListLink to={Routes.businessCalendars()}>Back to Your Calendars</BackToListLink>
        </NotFoundState>
      </Container>
    )
  }

  const { businessSubscription } = subscriptionOrg

  // Canceled subscriptions get a focused reactivate screen instead of the full
  // dashboard (and instead of the marketing landing). The calendar is paused.
  if (businessSubscription.status === 'canceled') {
    return <ReactivateCalendarScreen organizationName={subscriptionOrg.name} />
  }

  const isPastDue = businessSubscription.status === 'past_due'

  // Social tabs are admin-only for now (internal dogfooding); customer access will
  // be gated on the businessSubscription.contentFeed.enableSocialMedia entitlement.
  const socialEnabled = data?.currentUser?.isAdmin ?? false
  const activeSocialAccounts = subscriptionOrg.socialAccounts?.filter(a => a.status === 'active') || []
  const hasFacebook = activeSocialAccounts.some(a => a.platform === 'facebook_page')
  const hasInstagram = activeSocialAccounts.some(a => a.platform === 'instagram_business')

  const facebookState = socialEnabled ? (hasFacebook ? 'connected' : 'unlocked') : 'locked'
  const instagramState = socialEnabled ? (hasInstagram ? 'connected' : 'unlocked') : 'locked'

  const isNewSubscription = businessSubscription.trialActive
  const showOnboarding = isNewSubscription && !onboardingDismissed

  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true)
    localStorage.setItem(`${ONBOARDING_DISMISSED_KEY}_${organizationId}`, 'true')
  }

  return (
    <Container>
      <BackLink to={Routes.businessCalendars()}>
        <BackArrow />
        All Calendars
      </BackLink>
      <Header>
        <HeaderLeft>
          <Title>{subscriptionOrg.name} Calendar</Title>
          <StatusBadge status={businessSubscription.status}>
            {businessSubscription.trialActive
              ? `Trial (${businessSubscription.trialDaysRemaining} days left)`
              : humanizeSubscriptionStatus(businessSubscription.status)}
          </StatusBadge>
        </HeaderLeft>
        <SubmitEventLink href="/dash/event/add">Submit Event</SubmitEventLink>
      </Header>

      {isPastDue && isOrganizationOwner && <PastDueBanner organizationId={subscriptionOrg.id} />}

      {showOnboarding && (
        <OnboardingBanner>
          <OnboardingHeader>
            <OnboardingTitle>Get your calendar up and running</OnboardingTitle>
            <OnboardingDismiss onClick={handleDismissOnboarding}>&times;</OnboardingDismiss>
          </OnboardingHeader>
          <OnboardingSteps>
            <OnboardingStep onClick={() => {
              window.location.href = '/dash/event/add'
            }}>
              <OnboardingStepNumber>1</OnboardingStepNumber>
              <OnboardingStepContent>
                <OnboardingStepTitle>Submit an event</OnboardingStepTitle>
                <OnboardingStepText>Add events so your calendar has content to display.</OnboardingStepText>
              </OnboardingStepContent>
              <OnboardingStepArrow />
            </OnboardingStep>
            <OnboardingStep onClick={() => setActiveTab('branding')}>
              <OnboardingStepNumber>2</OnboardingStepNumber>
              <OnboardingStepContent>
                <OnboardingStepTitle>Customize your calendar</OnboardingStepTitle>
                <OnboardingStepText>Match your brand colors and preview how it looks.</OnboardingStepText>
              </OnboardingStepContent>
              <OnboardingStepArrow />
            </OnboardingStep>
            <OnboardingStep onClick={() => setActiveTab('embed')}>
              <OnboardingStepNumber>3</OnboardingStepNumber>
              <OnboardingStepContent>
                <OnboardingStepTitle>Embed on your website</OnboardingStepTitle>
                <OnboardingStepText>Copy a code snippet and add it to your site.</OnboardingStepText>
              </OnboardingStepContent>
              <OnboardingStepArrow />
            </OnboardingStep>
          </OnboardingSteps>
        </OnboardingBanner>
      )}

      <TabBar>
        <TabButton active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
          Analytics
        </TabButton>
        <TabButton active={activeTab === 'branding'} onClick={() => setActiveTab('branding')}>
          Customize
        </TabButton>
        <TabButton active={activeTab === 'embed'} onClick={() => setActiveTab('embed')}>
          Embed Code
        </TabButton>
        {socialEnabled && (
          <TabButton active={activeTab === 'social'} onClick={() => setActiveTab('social')}>
            Social Accounts
          </TabButton>
        )}
        {socialEnabled && (
          <TabButton active={activeTab === 'posts'} onClick={() => setActiveTab('posts')}>
            Social Posts
          </TabButton>
        )}
        {isOrganizationOwner && (
          <TabButton active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>
            Billing
          </TabButton>
        )}
      </TabBar>

      <TabContent>
        {socialMessage && (
          <SocialBanner type={socialMessage.type}>
            {socialMessage.text}
            <DismissButton onClick={() => setSocialMessage(null)}>&times;</DismissButton>
          </SocialBanner>
        )}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard
              organizationId={subscriptionOrg.id}
              networkSites={subscriptionOrg.networkSites}
              facebookState={facebookState}
              instagramState={instagramState}
              onNavigateToSocial={() => setActiveTab('social')}
              onNavigateToBilling={() => setActiveTab('billing')}
            />
        )}
        {activeTab === 'branding' && (
          <BrandingEditor
            organizationId={subscriptionOrg.id}
            brandingSettings={businessSubscription.brandingSettings}
            contentFeed={businessSubscription.contentFeed}
            onUpdate={() => refetch()}
          />
        )}
        {activeTab === 'embed' && (
          <EmbedCodeGenerator
            contentFeed={businessSubscription.contentFeed}
            brandingSettings={businessSubscription.brandingSettings}
          />
        )}
        {socialEnabled && activeTab === 'social' && <SocialAccountsManager organizationId={subscriptionOrg.id} />}
        {socialEnabled && activeTab === 'posts' && <SocialPostsManager organizationId={subscriptionOrg.id} />}
        {isOrganizationOwner && activeTab === 'billing' && <BillingSection organizationId={subscriptionOrg.id} subscription={businessSubscription} />}
      </TabContent>
    </Container>
  )
}

const BackArrow = () => (
  <Hugeicon name="arrow-left-long" size={16} />
)

export default BusinessCalendarDashboard

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 32px;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #102638;
  margin: 0;
`

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#dcfce7'
      case 'trialing':
        return '#dbeafe'
      case 'past_due':
        return '#fef3c7'
      default:
        return '#f3f4f6'
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#166534'
      case 'trialing':
        return '#1e40af'
      case 'past_due':
        return '#92400e'
      default:
        return '#374151'
    }
  }};
`

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 32px;
`

const TabButton = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ active }) => (active ? '#1e3a5f' : '#6b7280')};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? '#1e3a5f' : 'transparent')};
  border-radius: 0;
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #1e3a5f;
  }
`

const TabContent = styled.div``

const SetupState = styled.div`
  text-align: center;
  padding: 80px 32px;
`

const SetupSpinner = styled.div`
  width: 40px;
  height: 40px;
  margin: 0 auto 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const SetupTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 8px;
`

const SetupText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`

const SubmitEventLink = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 8px;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
    color: white;
  }
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  margin-bottom: 20px;
  transition: color 0.15s ease;

  &:hover {
    color: #374151;
  }
`

const NotFoundState = styled.div`
  text-align: center;
  padding: 80px 32px;
`

const NotFoundTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 8px;
`

const NotFoundText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
`

const BackToListLink = styled(Link)`
  display: inline-block;
  background: #1e3a5f;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s ease;

  &:hover {
    background: #2d4a6f;
    color: white;
  }
`

const SetupDelayIcon = () => (
  <Hugeicon name="clock" size={48} style={{ margin: '0 auto 16px', display: 'block', color: '#f59e0b' }} />
)

const SetupDelayActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`

const RefreshButton = styled.button`
  background: #1e3a5f;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2d4a6f;
  }
`

const SupportLink = styled.a`
  display: inline-block;
  background: white;
  color: #1e3a5f;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1e3a5f;
    color: #1e3a5f;
  }
`

const SocialBanner = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: ${({ type }) => (type === 'success' ? '#dcfce7' : '#fef2f2')};
  color: ${({ type }) => (type === 'success' ? '#166534' : '#991b1b')};
  border: 1px solid ${({ type }) => (type === 'success' ? '#bbf7d0' : '#fecaca')};
`

const DismissButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  padding: 0 4px;

  &:hover {
    opacity: 1;
  }
`

const OnboardingBanner = styled.div`
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4f8 100%);
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
`

const OnboardingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const OnboardingTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #102638;
  margin: 0;
`

const OnboardingDismiss = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: #374151;
  }
`

const OnboardingSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const OnboardingStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
  }
`

const OnboardingStepNumber = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1e3a5f;
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`

const OnboardingStepContent = styled.div`
  flex: 1;
  min-width: 0;
`

const OnboardingStepTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #102638;
  margin-bottom: 2px;
`

const OnboardingStepText = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`

const OnboardingStepArrow = () => (
  <Hugeicon name="arrow-right" size={14} style={{ flexShrink: 0, marginTop: 4, color: '#9ca3af' }} />
)

/* ── Celebration / Confetti ── */

const confettiFall = keyframes`
  0% {
    transform: translateY(-20px) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
`

const celebrationFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const CelebrationOverlay = styled.div`
  position: relative;
  text-align: center;
  padding: 80px 32px;
  overflow: hidden;
`

const ConfettiContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`

const ConfettiPiece = styled.div`
  position: absolute;
  top: -10px;
  border-radius: 2px;
  animation: ${confettiFall} 3s ease-in forwards;
`

const CelebrationContent = styled.div`
  position: relative;
  z-index: 1;
  animation: ${celebrationFadeIn} 0.5s ease-out;
`

const CelebrationIcon = styled.div`
  margin-bottom: 20px;
`

const CelebrationTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 12px;
`

const CelebrationText = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`

const CelebrationButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 28px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }
`

